<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { getLanguageName } from 'dashboard/components/widgets/conversation/advancedFilterItems/languages';
import ContactDetailsItem from './ContactDetailsItem.vue';
import CustomAttributes from './customAttributes/CustomAttributes.vue';

const props = defineProps({
  conversationAttributes: {
    type: Object,
    default: () => ({}),
  },
  contactAttributes: {
    type: Object,
    default: () => ({}),
  },
});

const { t } = useI18n();

const referer = computed(() => props.conversationAttributes.referer);
const initiatedAt = computed(
  () => props.conversationAttributes.initiated_at?.timestamp
);

const browserInfo = computed(() => props.conversationAttributes.browser);

const browserName = computed(() => {
  if (!browserInfo.value) return '';
  const { browser_name: name = '', browser_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const browserLanguage = computed(() =>
  getLanguageName(props.conversationAttributes.browser_language)
);

const platformName = computed(() => {
  if (!browserInfo.value) return '';
  const { platform_name: name = '', platform_version: version = '' } =
    browserInfo.value;
  return `${name} ${version}`;
});

const createdAtIp = computed(() => props.contactAttributes.created_at_ip);

const staticElements = computed(() =>
  [
    {
      content: initiatedAt,
      title: 'CONTACT_PANEL.INITIATED_AT',
      key: 'static-initiated-at',
      type: 'static_attribute',
    },
    {
      content: browserLanguage,
      title: 'CONTACT_PANEL.BROWSER_LANGUAGE',
      key: 'static-browser-language',
      type: 'static_attribute',
    },
    {
      content: referer,
      title: 'CONTACT_PANEL.INITIATED_FROM',
      key: 'static-referer',
      type: 'static_attribute',
    },
    {
      content: browserName,
      title: 'CONTACT_PANEL.BROWSER',
      key: 'static-browser',
      type: 'static_attribute',
    },
    {
      content: platformName,
      title: 'CONTACT_PANEL.OS',
      key: 'static-platform',
      type: 'static_attribute',
    },
    {
      content: createdAtIp,
      title: 'CONTACT_PANEL.IP_ADDRESS',
      key: 'static-ip-address',
      type: 'static_attribute',
    },
  ].filter(attribute => !!attribute.content.value)
);

const PLATFORM_I18N_MAP = {
  yandex_market: 'CONTACT_PANEL.MARKETPLACE.PLATFORMS.YANDEX_MARKET',
  ozon: 'CONTACT_PANEL.MARKETPLACE.PLATFORMS.OZON',
  wildberries: 'CONTACT_PANEL.MARKETPLACE.PLATFORMS.WILDBERRIES',
  rocketdata: 'CONTACT_PANEL.MARKETPLACE.PLATFORMS.ROCKETDATA',
};

const PLATFORM_DOMAINS = {
  yandex_market: 'market.yandex.ru',
  ozon: 'ozon.ru',
  wildberries: 'wildberries.ru',
  rocketdata: 'rocketdata.io',
};

const mpPlatform = computed(() => props.conversationAttributes.mp_platform);
const mpEntityType = computed(
  () => props.conversationAttributes.mp_entity_type
);
const mpSku = computed(() => props.conversationAttributes.mp_sku);
const mpRating = computed(() => props.conversationAttributes.mp_rating);
const mpExternalId = computed(
  () => props.conversationAttributes.mp_external_id
);
const mpOrderId = computed(() => props.conversationAttributes.mp_order_id);

const mpPlatformDisplay = computed(() => {
  const key = PLATFORM_I18N_MAP[mpPlatform.value];
  return key ? t(key) : mpPlatform.value;
});

const mpRatingDisplay = computed(() => {
  if (!mpRating.value) return '';
  return `${mpRating.value} ★`;
});

const mpExternalUrl = computed(() => {
  if (!mpExternalId.value || !mpPlatform.value) return '';
  const domain = PLATFORM_DOMAINS[mpPlatform.value];
  if (!domain) return '';
  return `https://${domain}/review/${mpExternalId.value}`;
});

const marketplaceElements = computed(() =>
  [
    {
      content: mpPlatformDisplay,
      title: 'CONTACT_PANEL.MARKETPLACE.PLATFORM',
      key: 'mp-platform',
    },
    {
      content: mpEntityType,
      title: 'CONTACT_PANEL.MARKETPLACE.ENTITY_TYPE',
      key: 'mp-entity-type',
    },
    {
      content: mpSku,
      title: 'CONTACT_PANEL.MARKETPLACE.SKU',
      key: 'mp-sku',
    },
    {
      content: mpRatingDisplay,
      title: 'CONTACT_PANEL.MARKETPLACE.RATING',
      key: 'mp-rating',
    },
    {
      content: mpExternalId,
      title: 'CONTACT_PANEL.MARKETPLACE.EXTERNAL_ID',
      key: 'mp-external-id',
    },
    {
      content: mpOrderId,
      title: 'CONTACT_PANEL.MARKETPLACE.ORDER_ID',
      key: 'mp-order-id',
    },
  ].filter(el => !!el.content.value)
);
</script>

<template>
  <div class="conversation--details">
    <div v-if="marketplaceElements.length > 0">
      <ContactDetailsItem
        v-for="element in marketplaceElements"
        :key="element.key"
        :title="$t(element.title)"
        :value="element.content.value"
      >
        <template v-if="element.key === 'mp-external-id' && mpExternalUrl">
          <a
            :href="mpExternalUrl"
            target="_blank"
            rel="noopener noreferrer nofollow"
            class="text-n-brand"
          >
            {{ element.content.value }}
          </a>
        </template>
      </ContactDetailsItem>
    </div>
    <CustomAttributes
      :static-elements="staticElements"
      attribute-class="conversation--attribute"
      attribute-from="conversation_panel"
      attribute-type="conversation_attribute"
    >
      <template #staticItem="{ element }">
        <ContactDetailsItem
          :key="element.title"
          :title="$t(element.title)"
          :value="element.content.value"
        >
          <a
            v-if="element.key === 'static-referer'"
            :href="element.content.value"
            rel="noopener noreferrer nofollow"
            target="_blank"
            class="text-n-brand"
          >
            {{ element.content.value }}
          </a>
        </ContactDetailsItem>
      </template>
    </CustomAttributes>
  </div>
</template>
